import { useFormikContext } from "formik";
import { Fragment, FunctionComponent, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DynamicFiltersContext, defaultContext } from "../dynamic-filters-context/dynamic-filters-context";
import { IFilterFieldOption, IProps } from "./dynamic-filter-group.types";
import {
    getNotVisibleFields,
    getValidFormChildren,
    getVisibleFields,
    sortFilterComponents,
} from "./dynamic-filter-group.utilities";
import { AddMoreFiltersButton } from "../add-more-filters-button";

/**
 * Wrapper for optional fields, included fields will be presented in a dropdown and only rendered if:
 * - they have a value at initialisation
 * - they are selected via the dropdown select
 * - they are one of the initial default fields passed as a prop.
 * @param props.canAddMoreFilters    Boolean indicating if more filters can be added.
 * @param props.canRemoveFilters     Boolean indicating if more filters can be removed.
 * @param props.children             List of DynamicFilter fields
 * @param props.hideFields           Fields that cannot be added.
 * @param props.initialVisibleFields Fields that should be displayed by default.
 * @param props.onRemoveFilter       An optional callback that runs when a filter is removed.
 * @param props.groupIcons           Icons for each group of filters, used to display the icon next to the group name.
 */
const DynamicFilterGroup: FunctionComponent<IProps> = ({
    canAddMoreFilters = true,
    canRemoveFilters = true,
    children,
    hideFields = [],
    groupIcons,
    initialVisibleFields = [],
    onRemoveFilter = () => {},
}) => {
    const { values: formValues } = useFormikContext<{ [index: string]: any }>();
    const filterComponents = useMemo(() => getValidFormChildren(children, hideFields), [children, hideFields]);
    const [visibleFields, setVisibleFields] = useState<string[]>(
        getVisibleFields(filterComponents, initialVisibleFields, formValues)
    );

    // Store this in a ref becuase if we try to use filterComponents directly in the useEffect below, it will cause
    // infinite re-renders
    const lastKnownFilterComponents = useRef(getValidFormChildren(children, hideFields));
    // If the formValues change, update the visible fields to reflect the new values.
    useEffect(() => {
        setVisibleFields((previousFields) => {
            return getVisibleFields(lastKnownFilterComponents.current, previousFields, formValues);
        });
    }, [formValues]);

    // Update the ref value when the children change.
    useEffect(() => {
        lastKnownFilterComponents.current = getValidFormChildren(children, hideFields);
    }, [children, hideFields]);

    // Fields not visible are available in a drop-down.
    const notVisibleFields = useMemo(
        () => getNotVisibleFields(filterComponents, visibleFields),
        [filterComponents, visibleFields]
    );

    /**
     * Order field components based on user selection
     */
    const sortedFieldComponents = useMemo(
        () => sortFilterComponents(filterComponents, visibleFields),
        [filterComponents, visibleFields]
    );

    /**
     * Handle form field selection, clear the selected field from all fields and put it in visible fields
     * @param selectedOption            Currently selected option
     */
    const handleSelectOptionalField = useCallback(
        (selectedOption: any): void => {
            if (selectedOption) {
                const [newOption] = notVisibleFields.filter((field) => field.name === selectedOption);
                setVisibleFields((previousFields) => [...previousFields, newOption.name]);
            }
        },
        [notVisibleFields]
    );

    return (
        <DynamicFiltersContext.Provider
            value={{
                ...defaultContext,
                canRemoveFilters,
                notVisibleFields,
                onRemoveFilter,
                setVisibleFields,
                visibleFields,
            }}
        >
            {sortedFieldComponents
                .filter((fieldComponent) => visibleFields.includes(fieldComponent.props.name))
                .map((fieldComponent) => (
                    <Fragment key={fieldComponent.props.name}>{fieldComponent}</Fragment>
                ))}

            {canAddMoreFilters && notVisibleFields.length > 0 && (
                <AddMoreFiltersButton<IFilterFieldOption>
                    data-testid="add-dynamic-filter"
                    getLabel={({ label, menuLabel }) => menuLabel ?? label}
                    getValue={({ name }) => name}
                    getGroup={({ group }) => group}
                    getGroupOrder={({ groupOrder }) => groupOrder}
                    getGroupItemOrder={({ groupItemOrder }) => groupItemOrder}
                    getGroupIcon={(group) => groupIcons?.[group]}
                    onClick={handleSelectOptionalField}
                    items={notVisibleFields}
                />
            )}
        </DynamicFiltersContext.Provider>
    );
};

export default memo(DynamicFilterGroup);
